import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enviar cookies httpOnly (auth) en cada request al backend.
  withCredentials: true,
});

// Normaliza err.response.data.detail cuando es un array de Pydantic ValidationError
// para que pueda renderizarse como string en toasts y mensajes.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msg = detail
          .map((d) => {
            if (typeof d === 'string') return d;
            const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
            return `${loc ? loc + ': ' : ''}${d?.msg || JSON.stringify(d)}`;
          })
          .join(' · ');
        error.response.data.detail = msg || 'Error de validación';
      } else if (detail && typeof detail === 'object') {
        // NO convertir a string si es un error estructurado (ej: conteo_posterior_existente)
        // que necesita ser procesado por el componente
        if (!detail.error) {
          error.response.data.detail = detail.msg || JSON.stringify(detail);
        }
        // Si tiene campo 'error', dejarlo como objeto para que el componente lo maneje
      }
    } catch (interceptorErr) {
      // El interceptor no debe romper el flujo: solo lo registramos.
      console.warn('Interceptor de errores falló:', interceptorErr);
    }
    return Promise.reject(error);
  }
);

// Lotes
export const getLotes = () => api.get('/lotes');
export const createLote = (data) => api.post('/lotes', data);
export const updateLote = (id, data) => api.put(`/lotes/${id}`, data);
export const deleteLote = (id) => api.delete(`/lotes/${id}`);

// Naves
export const getNaves = (loteId) => api.get('/naves', { params: loteId ? { lote_id: loteId } : {} });
export const createNave = (data) => api.post('/naves', data);
export const updateNave = (id, data) => api.put(`/naves/${id}`, data);
export const deleteNave = (id) => api.delete(`/naves/${id}`);

// Registros Diarios
export const getRegistrosDiarios = (params) => api.get('/registros-diarios', { params });
export const createRegistroDiario = (data) => api.post('/registros-diarios', data);
export const updateRegistroDiario = (id, data, params = {}) => api.put(`/registros-diarios/${id}`, data, { params });
export const deleteRegistroDiario = (id) => api.delete(`/registros-diarios/${id}`);
export const getRegistrosFaltantes = (params) => api.get('/produccion/registros-faltantes', { params });

// Inventario
export const getStock = () => api.get('/inventario/stock');
export const getInventarioHistorialDiario = (params) => api.get('/inventario/historial-diario', { params });
// Conteo Diario (verificación sin ajustar inventario)
export const getConteosDiarios = (params = {}) => api.get('/conteos-diarios', { params });
export const getConteoDiarioPorFecha = (fecha) => api.get(`/conteos-diarios/por-fecha/${fecha}`);
export const getConteoDiarioPreview = (params = {}) => api.get('/conteos-diarios/preview', { params });
export const upsertConteoDiario = (data) => api.post('/conteos-diarios', data);
export const deleteConteoDiario = (id) => api.delete(`/conteos-diarios/${id}`);
// Traslados entre tipos de huevo
export const crearTraslado = (data) => api.post('/inventario/traslado', data);
export const getTraslados = (params = {}) => api.get('/inventario/traslados', { params });
export const registrarSalida = (data) => api.post('/inventario/salida', data);
export const getMovimientos = (params) => api.get('/inventario/movimientos', { params });

// Trazabilidad por Lote (FIFO)
export const getInventarioPorLote = (params = {}) => api.get('/inventario/por-lote', { params });
export const getRentabilidadLote = (loteId) => api.get(`/lotes/${loteId}/rentabilidad`);
export const getRentabilidadComparativa = () => api.get('/analisis/rentabilidad-lotes');
export const getReporteSemanalLotes = (desde, hasta, comparar = false) =>
  api.get('/analisis/reporte-semanal-lotes', { params: { desde, hasta, comparar } });
export const createAbonoCliente = (payload) => api.post('/pagos/abono-cliente', payload);
export const getDesgloseLotesFactura = (facturaId) => api.get(`/facturas/${facturaId}/desglose-lotes`);

// Dashboard
export const getDashboardResumen = () => api.get('/dashboard/resumen');
export const getProduccionSemanal = (params) => api.get('/dashboard/produccion-semanal', { params });
export const getMortalidadTendencia = (params) => api.get('/dashboard/mortalidad-tendencia', { params });
export const getProduccionDiaria = (params) => api.get('/dashboard/produccion-diaria', { params });
export const getPosturaTotal = (params) => api.get('/dashboard/postura-total', { params });

// Clientes
export const getClientes = () => api.get('/clientes');
export const createCliente = (data) => api.post('/clientes', data);
export const updateCliente = (id, data) => api.put(`/clientes/${id}`, data);
export const deleteCliente = (id) => api.delete(`/clientes/${id}`);

// Precios
export const getPrecios = () => api.get('/precios');
export const updatePrecio = (data) => api.put('/precios', data);
export const updatePreciosBulk = (precios) => api.put('/precios/bulk', precios);

// Facturas
export const getFacturas = (params) => api.get('/facturas', { params });
export const createFactura = (data) => api.post('/facturas', data);
export const getFactura = (id) => api.get(`/facturas/${id}`);
export const updateFactura = (id, data, params = {}) => api.put(`/facturas/${id}`, data, { params });
export const deleteFactura = (id, force = false) => api.delete(`/facturas/${id}`, { params: force ? { force: true } : {} });
export const anularFactura = (id, motivo) => api.post(`/facturas/${id}/anular`, null, { params: motivo ? { motivo } : {} });
export const rellenarHuecosFacturas = () => api.post('/facturas/rellenar-huecos');
export const getEmpresaConfig = () => api.get('/configuracion/empresa');
export const updateEmpresaConfig = (data) => api.put('/configuracion/empresa', data);

// Categorias Gastos
export const getCategoriasGastos = () => api.get('/categorias-gastos');
export const createCategoriaGasto = (data) => api.post('/categorias-gastos', data);
export const deleteCategoriaGasto = (nombre) => api.delete(`/categorias-gastos/${encodeURIComponent(nombre)}`);

// Gastos
export const getGastos = (params) => api.get('/gastos', { params });
export const createGasto = (data) => api.post('/gastos', data);
export const updateGasto = (id, data) => api.put(`/gastos/${id}`, data);
export const deleteGasto = (id) => api.delete(`/gastos/${id}`);

// Config Costos
export const getConfigCostos = (loteId) => api.get(`/config-costos/${loteId}`);
export const updateConfigCostos = (data) => api.put('/config-costos', data);

// Costos Resumen / ROI
export const getCostosResumen = (loteId) => api.get(`/costos/resumen/${loteId}`);

// Conteos Semanales (Inventario Físico vs Sistema)
export const getConteos = () => api.get('/conteos-semanales');
export const getConteo = (id) => api.get(`/conteos-semanales/${id}`);
export const createConteo = (data) => api.post('/conteos-semanales', data);
export const updateConteo = (id, data) => api.put(`/conteos-semanales/${id}`, data);
export const deleteConteo = (id) => api.delete(`/conteos-semanales/${id}`);
export const getInventarioSistemaActual = (fecha) => api.get('/conteos-semanales/sistema-actual', { params: fecha ? { fecha } : {} });

// CxC (Cuentas por Cobrar)
export const getCxCResumen = () => api.get('/cxc/resumen');
export const getFacturasPendientes = (clienteId) => api.get('/facturas/pendientes', { params: clienteId ? { cliente_id: clienteId } : {} });
export const createPago = (data) => api.post('/pagos', data);
export const updatePago = (id, data) => api.put(`/pagos/${id}`, data);
export const deletePago = (id) => api.delete(`/pagos/${id}`);
export const deleteAbonoGrupo = (grupoId) => api.delete(`/pagos/abono/${grupoId}`);
export const getPagos = (params) => api.get('/pagos', { params });
export const getEstadoCuentaCliente = (clienteId) => api.get(`/cxc/estado-cuenta/${clienteId}`);
export const getCxCAging = () => api.get('/cxc/aging');

// Análisis de producción
export const getAnalisisProduccion = (params) => api.get('/analisis/produccion', { params });
export const getPosturaPorRegistro = (params) => api.get('/analisis/postura-por-registro', { params });

// Usuarios y Permisos (super_admin)
export const getUsers = () => api.get('/users');
export const updateUserPermisos = (id, modulos_permitidos) =>
  api.put(`/users/${id}/permisos`, { modulos_permitidos });
export const updateUserRole = (id, role) => api.put(`/users/${id}/role`, { role });
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const approveUser = (id) => api.post(`/users/${id}/aprobar`);
export const rejectUser = (id) => api.post(`/users/${id}/rechazar`);
export const getModulosDisponibles = () => api.get('/modulos-disponibles');

// Configuración del sistema (Super Código)
export const getSuperCodigo = () => api.get('/configuracion/super-codigo');
export const updateSuperCodigo = (valor) => api.put('/configuracion/super-codigo', { valor });
export const verificarSuperCodigo = (codigo) =>
  api.post('/configuracion/verificar-super-codigo', { codigo });

// Compras de huevos (entradas externas a inventario)
export const getCompras = (params) => api.get('/compras-huevos', { params });
export const createCompra = (data, params = {}) => api.post('/compras-huevos', data, { params });
export const updateCompra = (id, data) => api.put(`/compras-huevos/${id}`, data);
export const deleteCompra = (id) => api.delete(`/compras-huevos/${id}`);

// Rotos manuales (registro diario de roturas)
export const getRotos = (params) => api.get('/rotos', { params });
export const createRoto = (data) => api.post('/rotos', data);
export const updateRoto = (id, data) => api.put(`/rotos/${id}`, data);
export const deleteRoto = (id) => api.delete(`/rotos/${id}`);

// Comprobantes de pago (Supabase Storage)
export const uploadComprobantePago = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/pagos/upload-comprobante', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteComprobantePago = (url) =>
  api.delete('/pagos/comprobante', { params: { url } });

export default api;
